import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

interface DebugContextType {
  debugMode: boolean;
  toggleDebugMode: () => void;
  addDebugInfo: (key: string, data: any) => void;
  getDebugInfo: (key: string) => any;
  getAllDebugInfo: () => Record<string, any>;
  clearDebugInfo: () => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};

interface DebugProviderProps {
  children: React.ReactNode;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({ children }) => {
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});
  const [serverDebugMode, setServerDebugMode] = useState(false);
  const location = useLocation();

  // 从后端获取调试模式设置
  const fetchDebugMode = async () => {
    // 在登录页面或注册页面时不获取调试模式设置
    const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
    if (isAuthPage) {
      const localDebugMode = localStorage.getItem('debugMode') === 'true';
      setDebugMode(localDebugMode);
      setServerDebugMode(false);
      return;
    }

    try {
      // 先检查用户权限
      const userResponse = await api.get('/auth/me');
      const user = userResponse.data;
      
      if (user.role !== 'admin') {
        // 普通用户直接使用本地设置，不访问管理员API
        const localDebugMode = localStorage.getItem('debugMode') === 'true';
        setDebugMode(localDebugMode);
        setServerDebugMode(false);
        return;
      }
      
      // 管理员用户访问系统配置
      const response = await api.get('/admin/system/config');
      const serverDebugModeValue = response.data.debug_mode;
      setServerDebugMode(serverDebugModeValue);
      
      // 如果后端设置了调试模式，则启用
      if (serverDebugModeValue) {
        setDebugMode(true);
        localStorage.setItem('debugMode', 'true');
      } else {
        // 如果后端关闭了调试模式，则禁用并清除调试信息
        setDebugMode(false);
        localStorage.setItem('debugMode', 'false');
        setDebugInfo({}); // 清除调试信息
        localStorage.removeItem('globalDebugInfo');
      }
    } catch (error: any) {
      console.warn('无法从服务器获取调试模式设置:', error);
      
      // 如果是权限错误，使用本地存储的设置
      if (error.response?.status === 403) {
        const localDebugMode = localStorage.getItem('debugMode') === 'true';
        setDebugMode(localDebugMode);
        setServerDebugMode(false); // 假设服务器调试模式为false
      }
    }
  };

  const toggleDebugMode = () => {
    const newMode = !debugMode;
    setDebugMode(newMode);
    localStorage.setItem('debugMode', newMode.toString());
    
    // 如果后端设置了调试模式，则不允许用户手动关闭
    if (serverDebugMode && !newMode) {
      console.warn('后端已启用调试模式，无法手动关闭');
      setDebugMode(true);
      localStorage.setItem('debugMode', 'true');
    }
  };

  const addDebugInfo = (key: string, data: any) => {
    // 只有在调试模式启用且后端允许时才添加调试信息
    if (debugMode && serverDebugMode) {
      setDebugInfo(prev => ({
        ...prev,
        [key]: {
          data,
          timestamp: new Date().toISOString()
        }
      }));
    }
  };

  const getDebugInfo = (key: string) => {
    return debugInfo[key]?.data;
  };

  const getAllDebugInfo = () => {
    return debugInfo;
  };

  const clearDebugInfo = () => {
    setDebugInfo({});
    localStorage.removeItem('globalDebugInfo');
  };

  // 监听键盘快捷键 Ctrl+Shift+D 切换调试模式
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        toggleDebugMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleDebugMode]);

  // 监听API调试信息事件
  useEffect(() => {
    const handleDebugInfo = (event: CustomEvent) => {
      const { key, data } = event.detail;
      setDebugInfo(prev => ({
        ...prev,
        [key]: data
      }));
    };

    window.addEventListener('debug-info', handleDebugInfo as EventListener);
    return () => window.removeEventListener('debug-info', handleDebugInfo as EventListener);
  }, []);

  // 初始化时从localStorage恢复调试模式状态，并从后端获取设置
  useEffect(() => {
    const savedDebugMode = localStorage.getItem('debugMode') === 'true';
    setDebugMode(savedDebugMode);
    
    // 从localStorage恢复调试信息
    const savedDebugInfo = JSON.parse(localStorage.getItem('globalDebugInfo') || '{}');
    setDebugInfo(savedDebugInfo);
    
    // 从后端获取调试模式设置
    fetchDebugMode();
    
    // 设置定期检查后端调试模式设置（每5分钟检查一次）
    const interval = setInterval(fetchDebugMode, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [location.pathname]);

  return (
    <DebugContext.Provider
      value={{
        debugMode,
        toggleDebugMode,
        addDebugInfo,
        getDebugInfo,
        getAllDebugInfo,
        clearDebugInfo
      }}
    >
      {children}
    </DebugContext.Provider>
  );
};
